/**
 * GA4 Integration
 * Waits for sales channel to load gtag, configures enhanced conversions,
 * and tracks events with user data enrichment.
 *
 * Standalone version for co-aurora.
 * Ported from shopper/src/scripts/lib/integrations/ga4.js
 */
(function() {
  'use strict';

  function Ga4(config) {
    this.measurementId = config.measurementId;
    this.data = Object.assign({}, window.theme || {});
    window.TrackingBase.call(this, Object.assign({
      name: 'GA4',
      enableEcommerce: true,
      enableDeduplication: true
    }, config));
  }

  Ga4.prototype = Object.create(window.TrackingBase.prototype);
  Ga4.prototype.constructor = Ga4;

  Ga4.prototype.initialize = function() {
    if (!this.measurementId || this.measurementId === 'undefined') {
      this.warn('GA4 Measurement ID missing - integration disabled');
      this.config.enabled = false;
      return;
    }

    this._setupAjaxEventListeners();
    this._attachEventListeners();
    this._checkForGtagPeriodically();
  };

  Ga4.prototype._checkForGtagPeriodically = function() {
    var self = this;
    var attempts = 0;
    var maxAttempts = 200;

    var interval = setInterval(function() {
      attempts++;

      if (window.gtag && typeof window.gtag === 'function' && attempts > 20) {
        self.log('Sales channel GA4 confirmed after ' + (attempts * 100) + 'ms');
        self._completeGtagSetup();
        clearInterval(interval);
        return;
      }

      if (attempts >= maxAttempts) {
        if (window.gtag) self._completeGtagSetup();
        clearInterval(interval);
      }
    }, 100);
  };

  Ga4.prototype._completeGtagSetup = function() {
    this.tagLoaded = true;
    if (!window.gtag) return;

    var configOptions = { 'allow_enhanced_conversions': true };
    var customerData = this.getCustomerData();
    if (customerData.customer_id) {
      configOptions.user_id = customerData.customer_id.toString();
    }

    window.gtag('config', this.measurementId, configOptions);
    this._configureEnhancedConversions();
  };

  Ga4.prototype._configureEnhancedConversions = function() {
    try {
      var userData = {};

      /* Client IP */
      if (window.AttributionManager) {
        var sessionData = window.AttributionManager.getSessionData();
        if (sessionData && sessionData.client_ip) {
          userData.ip_address = sessionData.client_ip;
          userData.user_agent = navigator.userAgent;
        }
      }

      var customer = window.theme && window.theme.customer;
      if (customer && customer.id) {
        if (customer.email) userData.email = customer.email;
        if (customer.phone) {
          var phone = customer.phone;
          if (phone.length === 10 && phone.charAt(0) !== '+') phone = '+1' + phone;
          else if (phone.charAt(0) !== '+' && phone.length > 10) phone = '+' + phone;
          userData.phone_number = phone;
        }
        if (customer.first_name) userData.first_name = customer.first_name;
        if (customer.last_name) userData.last_name = customer.last_name;
        if (customer.street) userData.street = customer.street;
        if (customer.city) userData.city = customer.city;
        if (customer.state) userData.region = customer.state;
        if (customer.postal_code) userData.postal_code = customer.postal_code;
        if (customer.country) userData.country = customer.country.toUpperCase();
      }

      /* Attribution enrichment */
      if (window.AttributionManager) {
        var googleData = window.AttributionManager.getGoogleData();
        var attrCustomer = window.AttributionManager.getCustomerData();

        if (!userData.email && attrCustomer.email) userData.email = attrCustomer.email;
        if (!userData.phone_number && attrCustomer.phone) {
          var p = attrCustomer.phone;
          if (p.length === 10 && p.charAt(0) !== '+') p = '+1' + p;
          else if (p.charAt(0) !== '+' && p.length > 10) p = '+' + p;
          userData.phone_number = p;
        }
        if (!userData.first_name && attrCustomer.first_name) userData.first_name = attrCustomer.first_name;
        if (!userData.last_name && attrCustomer.last_name) userData.last_name = attrCustomer.last_name;
        if (!userData.city && attrCustomer.city) userData.city = attrCustomer.city;
        if (!userData.region && attrCustomer.state) userData.region = attrCustomer.state;
        if (!userData.postal_code && attrCustomer.zip) userData.postal_code = attrCustomer.zip;
        if (!userData.country && attrCustomer.country) userData.country = attrCustomer.country.toUpperCase();

        if (googleData.gclid) window.gtag('set', { 'gclid': googleData.gclid });
        if (googleData.gbraid) window.gtag('set', { 'gbraid': googleData.gbraid });
        if (googleData.wbraid) window.gtag('set', { 'wbraid': googleData.wbraid });
      }

      if (Object.keys(userData).length > 0) return userData;
      return null;
    } catch (err) {
      this.warn('Failed to configure enhanced conversions', err);
      return null;
    }
  };

  Ga4.prototype.track = function(eventName, eventData, options) {
    options = options || {};
    var self = this;

    if (!this.tagLoaded && window.gtag) this._completeGtagSetup();

    if (!window.gtag) {
      return new Promise(function(resolve, reject) {
        var retries = 0;
        var max = 10;
        (function retry() {
          if (window.gtag) { resolve(); return; }
          retries++;
          if (retries >= max) { reject(new Error('gtag not available')); return; }
          setTimeout(retry, 500);
        })();
      }).then(function() { return doTrack(); });
    }
    return Promise.resolve(doTrack());

    function doTrack() {
      var userData = self._configureEnhancedConversions();
      var gaEventData = self._formatEventDataForGA(eventData, options);

      if (window.AttributionManager) {
        var gd = window.AttributionManager.getGoogleData();
        if (gd.gclid) gaEventData.gclid = gd.gclid;
        if (gd.gbraid) gaEventData.gbraid = gd.gbraid;
        if (gd.wbraid) gaEventData.wbraid = gd.wbraid;
        if (gd.client_id) gaEventData.ga_client_id = gd.client_id;
        if (gd.session_id) gaEventData.ga_session_id = gd.session_id;
      }

      if (userData && Object.keys(userData).length > 0) gaEventData.user_data = userData;

      var cd = self.getCustomerData();
      if (cd.customer_id) gaEventData.user_id = cd.customer_id.toString();

      window.gtag('event', eventName, gaEventData);
      self.log('Tracked ' + eventName, { eventId: options.eventID });
    }
  };

  Ga4.prototype._formatEventDataForGA = function(eventData, options) {
    var formatted = {};
    if (eventData.value !== undefined) formatted.value = eventData.value;
    if (eventData.currency) formatted.currency = eventData.currency;
    if (eventData.transaction_id) formatted.transaction_id = eventData.transaction_id;
    if (eventData.coupon) formatted.coupon = eventData.coupon;
    if (eventData.shipping) formatted.shipping = eventData.shipping;
    if (eventData.tax) formatted.tax = eventData.tax;
    if (eventData.search_term) formatted.search_term = eventData.search_term;

    if (eventData.items && Array.isArray(eventData.items)) {
      formatted.items = eventData.items.map(function(item) {
        return {
          item_id: item.item_id || item.id,
          item_name: item.item_name || item.name,
          item_category: item.item_category || item.category,
          item_brand: item.item_brand || item.brand,
          item_variant: item.item_variant || item.variant,
          price: parseFloat(item.price || 0),
          quantity: parseInt(item.quantity || 1, 10)
        };
      });
    }

    /* Custom parameters */
    var standardParams = [
      'currency', 'value', 'transaction_id', 'coupon', 'shipping', 'tax', 'items',
      'search_term', 'page_title', 'page_location', 'page_referrer',
      'gclid', 'gbraid', 'wbraid', 'ga_client_id', 'ga_session_id', 'user_id'
    ];
    Object.keys(eventData).forEach(function(key) {
      if (!formatted[key] && standardParams.indexOf(key) === -1) {
        var val = eventData[key];
        if (typeof val === 'string' && val.length > 100) val = val.substring(0, 97) + '...';
        formatted[key] = val;
      }
    });

    if (options && options.eventID) formatted.event_id = options.eventID;
    return formatted;
  };

  /* Export */
  window.TrackingGA4 = Ga4;
})();
